import { type ReactNode } from 'react';
import { type ParseKeys, type TFunction } from 'i18next';
import {
  CountryCode,
  isFullListing,
  type UserAccountExclusiveListing,
  type BigDecimalRangeDto,
  type Language,
  type Listing,
  type SearchListing,
  type WithOptional,
} from '@pkgs/api';

import { formatDate } from '@/utils/formatters';
import { SurfaceTotalIcon } from '@/icons/small/SurfaceTotal';
import { SurfaceTerraceIcon } from '@/icons/small/SurfaceTerrace';
import { SurfaceStorageIcon } from '@/icons/small/SurfaceStorage';
import { SurfaceSalesIcon } from '@/icons/small/SurfaceSales';
import { SurfacePlotIcon } from '@/icons/small/SurfacePlot';
import { SurfaceOfficeIcon } from '@/icons/small/SurfaceOffice';
import { SurfaceLivingShopIcon } from '@/icons/small/SurfaceLivingShop';
import { SurfaceDividableIcon } from '@/icons/small/SurfaceDividable';
import { RoomsIcon } from '@/icons/small/Rooms';
import { PropertyForSale } from '@/icons/small/PropertyForSale';
import { PropertyBuilding } from '@/icons/small/PropertyBuilding';
import { FlagIcon } from '@/icons/small/Flag';
import { BedroomPropertyIcon } from '@/icons/small/BedroomProperty';
import { BathroomPropertyIcon } from '@/icons/small/BathroomProperty';

export type KeyInfoFormatOptions = {
  isSurface?: boolean;
  shouldShowRoundedValue?: boolean;
};

export type KeyInfoData = {
  labelKey: ParseKeys<'property_expose'>;
  id: string;
  value: number | string | BigDecimalRangeDto | undefined;
  icon?: ReactNode;
  formatValue?: (value: number | string | BigDecimalRangeDto) => string;
  formatWithLabel?: (
    value: number | string | BigDecimalRangeDto,
    labelKey: ParseKeys<'property_expose'>,
    t: TFunction<'property_expose'>,
  ) => string;
  formatOptions?: KeyInfoFormatOptions;
};

export const getListingKeyInfoData = (
  property: Listing | SearchListing | UserAccountExclusiveListing,
  activeUnits: number,
  language: Language,
): KeyInfoData[] => {
  const items: WithOptional<KeyInfoData, 'value'>[] = [
    {
      labelKey: 'search.expose.propertyDetails.label.units',
      id: 'totalUnits',
      value: property.totalUnits,
      icon: <PropertyBuilding />,
    },
    {
      labelKey: 'search.expose.propertyDetails.label.unitsSale',
      id: 'activeUnits',
      value: activeUnits > 0 ? activeUnits : undefined,
      icon: <PropertyForSale />,
    },
    {
      labelKey: 'search.expose.propertyDetails.label.rooms',
      id: 'rooms',
      value: property.rooms,
      icon: <RoomsIcon />,
    },
    {
      labelKey: 'search.expose.propertyDetails.label.bedrooms',
      id: 'bedrooms',
      value: property.bedrooms,
      icon: <BedroomPropertyIcon />,
    },
    {
      labelKey: 'search.expose.propertyDetails.label.bathrooms',
      id: 'bathrooms',
      value: property.bathrooms,
      icon: <BathroomPropertyIcon />,
    },
    {
      labelKey: 'search.expose.propertyDetails.label.totalSurface',
      id: 'totalSurface',
      value: property.area?.totalSurface,
      icon: <SurfaceTotalIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: true },
    },
    {
      labelKey: 'search.expose.propertyDetails.label.livingArea',
      id: 'livingSurface',
      value: property.area?.livingSurface,
      icon: <SurfaceLivingShopIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: true },
    },
    {
      labelKey: 'search.expose.propertyDetails.label.plotSurface',
      id: 'plotSurface',
      value: property.area?.plotSurface,
      icon: <SurfacePlotIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: property.countryAlpha2 !== CountryCode.CH },
    },
    {
      labelKey: 'search.expose.propertyDetails.label.terraceSurface',
      id: 'terraceSurface',
      value: property.area?.terraceSurface,
      icon: <SurfaceTerraceIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: true },
    },
    {
      labelKey: 'search.expose.propertyDetails.label.storageSurface',
      id: 'storageSurface',
      value: property.area?.storageSurface,
      icon: <SurfaceStorageIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: true },
    },
    {
      labelKey: 'search.expose.propertyDetails.label.shopSurface',
      id: 'shopSurface',
      value: property.area?.shopSurface,
      icon: <SurfaceLivingShopIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: true },
    },
    {
      labelKey: 'search.expose.propertyDetails.label.officeSurface',
      id: 'officeSurface',
      value: property.area?.officeSurface,
      icon: <SurfaceOfficeIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: true },
    },
    {
      labelKey: 'search.expose.propertyDetails.label.salesSurface',
      id: 'salesSurface',
      value: property.area?.salesSurface,
      icon: <SurfaceSalesIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: true },
    },
    {
      labelKey: 'search.expose.propertyDetails.label.dividableFrom',
      id: 'dividableFrom',
      value: property.area?.dividableFrom,
      icon: <SurfaceDividableIcon />,
      formatOptions: { isSurface: true, shouldShowRoundedValue: true },
      formatWithLabel: (value, labelKey, t) => `${t(labelKey)} ${value}`,
    },
  ];
  if (isFullListing(property) && ('availableFrom' in property || 'constructionYear' in property)) {
    if (property.availableFrom) {
      items.push({
        labelKey: 'search.expose.propertyDetails.label.availableFrom',
        id: 'availableFrom',
        value: property.availableFrom,
        icon: <FlagIcon />,
        formatValue: (value) => formatDate(value as string, language),
        formatWithLabel: (value, labelKey, t) => `${t(labelKey)} ${value}`,
      });
    } else if (property.constructionYear) {
      items.push({
        labelKey: 'search.expose.propertyDetails.label.constructionYear',
        id: 'constructionYear',
        value: property.constructionYear.min,
        icon: <FlagIcon />,
        formatValue: (value) => value.toString(),
        formatWithLabel: (value, labelKey, t) => `${t(labelKey)} ${value}`,
      });
    }
  }

  return items.filter((item) => item.value !== undefined) as KeyInfoData[];
};
